import { getAddress, keccak256, toBytes, type Address } from "viem";
import type { DeploymentReference } from "./public-v2";
import { encodeV2Utf8Prompt, type V2Utf8PromptPlan } from "./v2-prompt-codec";
import { parsePrivateAgentStatus } from "../../../hosting/packages/shared/src/private-agent-status.js";
import type { PrivateAgentStatus } from "../../../hosting/packages/shared/src/private-agent-status.js";

export { parsePrivateAgentStatus, runtimeStatusIsDisplayFresh } from "../../../hosting/packages/shared/src/private-agent-status.js";
export type { AgentHostingState, PrivateAgentStatus } from "../../../hosting/packages/shared/src/private-agent-status.js";

export const CONTROL_ORIGIN = "https://control.botnet.family";
export const AUTH_CHAIN_ID = 8453;
export const CONTROL_AUTH_PATHS = {
  challenge: "/v2/control/auth/challenge",
  verify: "/v2/control/auth/verify",
  session: "/v2/control/auth/session",
  logout: "/v2/control/auth/logout",
} as const;
const CAPABILITIES_PATH = "/v2/control/capabilities";
const CLIENT_MARKER = "1";
const REQUEST_TIMEOUT_MS = 8_000;
const RESERVATION_PATH = "/v2/control/reservations";
const RESERVATION_DISCOVERY_MAX_BYTES = 128 * 1024;
const RESERVATION_MAX_BYTES = 16_384;
const MAX_RESERVATION_BODY_BYTES = 98_304;
const MAX_AGENT_ID = (1n << 256n) - 1n;
const CSRF_PATTERN = /^[A-Za-z0-9_-]{42}[AEIMQUYcgkosw048]$/;
const API_ERRORS = new Set(["invalid_request", "unauthorized", "forbidden", "expired", "invalid_signature", "quota", "unavailable"]);
const API_ERROR_STATUS: Record<string, number> = {
  invalid_request: 400,
  unauthorized: 401,
  forbidden: 403,
  expired: 401,
  invalid_signature: 401,
  quota: 429,
  unavailable: 503,
};
const ERROR_MESSAGES: Record<string, string> = {
  invalid_request: "The sign-in request was invalid.",
  unauthorized: "Your sign-in session is no longer valid.",
  forbidden: "The sign-in request could not be authorized.",
  expired: "The sign-in request expired. Try again.",
  invalid_signature: "The wallet signature could not be verified.",
  quota: "Sign-in is temporarily rate limited. Try again shortly.",
  unavailable: "Wallet sign-in is temporarily unavailable.",
};
const RESERVATION_ERROR_MESSAGES: Record<WalletReservationErrorCode, string> = {
  disabled: "Reservation requests are disabled.",
  invalid_request: "The reservation request was invalid.",
  unauthorized: "Your reservation session is no longer valid.",
  forbidden: "The reservation request could not be authorized.",
  expired: "The reservation session expired. Try again.",
  invalid_signature: "The reservation proof could not be verified.",
  quota: "Reservation service is temporarily rate limited. Try again shortly.",
  conflict: "The reservation request conflicts with an existing request.",
  not_found: "The reservation was not found.",
  unavailable: "Reservation service is temporarily unavailable.",
  invalid_response: "Reservation service returned an invalid response.",
  timeout: "The reservation request timed out.",
  cancelled: "The reservation request was cancelled.",
};

export class WalletAuthorizationError extends Error {
  constructor(readonly code: string) {
    super(ERROR_MESSAGES[code] ?? "Wallet sign-in is temporarily unavailable.");
    this.name = "WalletAuthorizationError";
  }
}

export type WalletAuthFetch = typeof fetch;
export interface WalletAuthCapabilities {
  readonly deployment: DeploymentReference | null;
  readonly walletAuthentication: boolean;
  readonly privateAgentControls: false;
  readonly privateAgentReads: boolean;
  readonly admission: boolean;
  readonly payments: boolean;
  readonly configuredTierIds: readonly number[];
}
export interface WalletSessionDescriptor {
  schemaVersion: 2;
  address: Address;
  chainId: 8453;
  expiresAtMs: number;
  idleExpiresAtMs: number;
  csrfToken: string;
}
export interface V2AdmissionVoucher {
  readonly creator: Address; readonly payer: Address; readonly reservationId: `0x${string}`;
  readonly capacitySlotId: `0x${string}`; readonly slotEpoch: string; readonly tierId: number;
  readonly price: string; readonly feeReceiver: Address; readonly nameHash: `0x${string}`;
  readonly promptEncoding: 0; readonly promptChunks: number; readonly promptRoot: `0x${string}`;
  readonly encodedPromptDigest: `0x${string}`; readonly decodedPromptDigest: `0x${string}`;
  readonly encodedPromptBytes: number; readonly decodedPromptBytes: number;
  readonly systemPromptHash: `0x${string}`; readonly runtimePolicyHash: `0x${string}`;
  readonly accountPolicyHash: `0x${string}`; readonly settlementPolicyHash: `0x${string}`;
  readonly quoteSignerEpoch: string; readonly quoteNonce: string; readonly issuedAt: string;
  readonly payBefore: string; readonly uploadBefore: string; readonly fulfillBefore: string;
}
export interface V2ReservationRequest {
  readonly schemaVersion: 2;
  readonly idempotencyKey: string;
  readonly tierId: number;
  readonly name: string;
  readonly additionalPrompt: { readonly encoding: 0; readonly encodedBase64: string };
}
export type WalletReservationResult =
  | Readonly<{ kind: "voucher"; schemaVersion: 2; reservationId: `0x${string}`; state: "quoted" | "paid"; voucher: V2AdmissionVoucher; signature: `0x${string}`; deployment: DeploymentReference }>
  | Readonly<{ kind: "state"; schemaVersion: 2; reservationId: `0x${string}`; state: "paid" | "converted" | "released" }>;
export type WalletReservationErrorCode = "disabled" | "invalid_request" | "unauthorized" | "forbidden" | "expired" |
  "invalid_signature" | "quota" | "conflict" | "not_found" | "unavailable" | "invalid_response" | "timeout" | "cancelled";
export class WalletReservationError extends Error {
  constructor(readonly code: WalletReservationErrorCode, readonly mayHaveReserved: boolean) {
    super(RESERVATION_ERROR_MESSAGES[code]);
    this.name = "WalletReservationError";
  }
}
export interface WalletReservationAttemptInput {
  readonly session: WalletSessionDescriptor;
  readonly idempotencyKey: string;
  readonly tierId: number;
  readonly name: string;
  readonly additionalPrompt: string;
  readonly systemPromptHash: `0x${string}`;
}
export interface WalletReservationAttempt {
  readonly request: V2ReservationRequest;
  resolve(operation: WalletOperation): Promise<WalletReservationIdempotencyBinding>;
  submit(operation: WalletOperation, expectedReservationId?: `0x${string}`): Promise<WalletReservationResult>;
  read(reservationId: `0x${string}`, operation: WalletOperation): Promise<WalletReservationResult>;
}
export type WalletReservationIdempotencyBinding = Readonly<{
  schemaVersion: 2;
  reservationId: `0x${string}`;
  state: WalletReservationRecoveryStatus;
  requestHash: `0x${string}`;
  deployment: DeploymentReference;
}>;
export type WalletReservationRecoveryStatus = "signing" | "quoted" | "paid" | "reconciling" | "released" | "converted";
export type WalletReservationRecoveryScope = "configured_deployment" | "foreign_deployment";
export type WalletReservationRecoveryRecord = Readonly<{
  reservationId: `0x${string}`;
  payer: `0x${string}`;
  status: WalletReservationRecoveryStatus;
  createdAtMs: number;
  deployment: DeploymentReference;
  scope: WalletReservationRecoveryScope;
}>;
export type WalletReservationRecoverySnapshot = Readonly<{
  schemaVersion: 2;
  records: readonly WalletReservationRecoveryRecord[];
}>;
export interface WalletAuthClientOptions {
  enabled: boolean;
  deployment?: DeploymentReference | null;
  fetch?: WalletAuthFetch;
}
export interface WalletAuthClient {
  challenge(address: string, signal: AbortSignal): Promise<{ message: string; preauthCsrfToken: string }>;
  verify(address: string, message: string, signature: string, preauthCsrfToken: string, signal: AbortSignal): Promise<WalletSessionDescriptor>;
  session(address: string, csrfToken: string, signal: AbortSignal): Promise<WalletSessionDescriptor>;
  logout(csrfToken: string, signal?: AbortSignal): Promise<void>;
  privateAgentStatus(registryAddress: string, agentId: string, csrfToken: string, signal: AbortSignal): Promise<PrivateAgentStatus>;
  listWalletReservations(session: WalletSessionDescriptor, operation: WalletOperation): Promise<WalletReservationRecoverySnapshot>;
  reservationAttempt(input: WalletReservationAttemptInput): WalletReservationAttempt;
}

export type WalletProgressCode = import("./launch-activity").WalletProgressCode;

export interface WalletOperation {
  signal: AbortSignal;
  isCurrent(): boolean;
  finish(): void;
  reportProgress?: (code: WalletProgressCode) => unknown;
}

/** Best-effort progress reporting must never alter wallet-operation control flow. */
export function reportWalletProgress(operation: WalletOperation, code: WalletProgressCode): void {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(operation, "reportProgress");
    if (!descriptor || !("value" in descriptor) || typeof descriptor.value !== "function") return;
    const result: unknown = Reflect.apply(descriptor.value, undefined, [code]);
    if (result !== null && (typeof result === "object" || typeof result === "function")) {
      try { void Promise.resolve(result).catch(() => undefined); } catch { /* A broken thenable is advisory only. */ }
    }
  } catch { /* Progress reporting must not change the operation result. */ }
}

/** Owns browser-side wallet work so identity changes can abort requests and fence late replies. */
export class WalletOperationGuard {
  private generation = 0;
  private active: AbortController | null = null;

  begin(): WalletOperation {
    this.invalidate();
    const generation = this.generation;
    const controller = new AbortController();
    this.active = controller;
    const isCurrent = () => generation === this.generation && !controller.signal.aborted;
    return {
      signal: controller.signal,
      isCurrent,
      finish: () => {
        if (this.active === controller) this.active = null;
      },
    };
  }

  invalidate(): void {
    this.generation += 1;
    this.active?.abort();
    this.active = null;
  }
}

export { reviewV2Quote, V2QuoteReviewError } from "./v2-quote-review";
export type {
  V2QuoteReadAdapter,
  V2QuoteReviewErrorCode,
  V2QuoteReviewInput,
  V2QuoteReviewObservation,
} from "./v2-quote-review";

export function parseCapabilities(value: unknown, expectedDeployment: DeploymentReference | null): WalletAuthCapabilities | null {
  if (!isRecord(value)) return null;
  const keys = Object.keys(value).sort().join(",");
  if (value.schemaVersion !== 2 || keys !== "admission,apiVersion,configuredTierIds,deployment,mail,payments,posting,privateAgentControls,privateAgentReads,schemaVersion,service,verifiedRuntimeHealth,walletAuthentication" ||
      value.service !== "control-gateway" || value.apiVersion !== "wallet-control-v2" ||
      typeof value.walletAuthentication !== "boolean" || typeof value.privateAgentReads !== "boolean" || value.privateAgentControls !== false ||
      typeof value.admission !== "boolean" || typeof value.payments !== "boolean" || (value.payments && !value.admission) ||
      ((value.admission || value.payments) && (!value.walletAuthentication || value.deployment === null)) ||
      value.mail !== false || value.posting !== false || value.verifiedRuntimeHealth !== false ||
      (value.privateAgentReads && !value.walletAuthentication)) return null;
  if (!Array.isArray(value.configuredTierIds) || value.configuredTierIds.length > 128) return null;
  const configuredTierIds: number[] = [];
  let previousTierId = -1;
  for (const tierId of value.configuredTierIds) {
    if (typeof tierId !== "number" || !Number.isSafeInteger(tierId) || tierId < 0 || tierId > 0xffff_ffff || tierId <= previousTierId) return null;
    configuredTierIds.push(tierId);
    previousTierId = tierId;
  }
  let deployment: DeploymentReference | null;
  if (value.deployment === null) deployment = null;
  else if (isDeploymentReference(value.deployment)) deployment = { ...value.deployment };
  else return null;
  if (deployment === null && configuredTierIds.length !== 0) return null;
  if (!sameDeployment(deployment, expectedDeployment)) return null;
  if (deployment) Object.freeze(deployment);
  Object.freeze(configuredTierIds);
  return Object.freeze({ deployment, walletAuthentication: value.walletAuthentication, privateAgentControls: false, privateAgentReads: value.privateAgentReads,
    admission: value.admission, payments: value.payments, configuredTierIds });
}

function unavailableCapabilities(): WalletAuthCapabilities {
  return Object.freeze({ deployment: null, walletAuthentication: false, privateAgentControls: false, privateAgentReads: false,
    admission: false, payments: false, configuredTierIds: Object.freeze([]) });
}

export async function readWalletAuthCapabilities(options: {
  demo: boolean;
  publicControlOrigin?: string | null;
  publicDeployment?: DeploymentReference | null;
  fetch?: WalletAuthFetch;
  signal?: AbortSignal;
}): Promise<WalletAuthCapabilities> {
  if (options.demo || options.publicControlOrigin !== CONTROL_ORIGIN || !options.publicDeployment) {
    return unavailableCapabilities();
  }
  let parsed: WalletAuthCapabilities | null;
  try {
    parsed = await boundedRequest(async (signal) => {
      const response = await (options.fetch ?? fetch)(`${CONTROL_ORIGIN}${CAPABILITIES_PATH}`, {
        method: "GET",
        credentials: "omit",
        cache: "no-store",
        redirect: "error",
        headers: { accept: "application/json" },
        signal,
      });
      if (response.status !== 200) return null;
      return parseCapabilities(await readJson(response, 4096), options.publicDeployment ?? null);
    }, options.signal);
  } catch { parsed = null; }
  return parsed ?? unavailableCapabilities();
}

export function parseCanonicalChallenge(message: string, address: string): boolean {
  let canonical: Address;
  try { canonical = getAddress(address); } catch { return false; }
  const lines = message.split("\n");
  if (lines.length !== 13 || lines[0] !== "botnet.family wants you to sign in with your Ethereum account:" ||
      lines[1] !== canonical || lines[2] !== "" || lines[3] !== "Sign in to botnet.family." || lines[4] !== "" ||
      lines[5] !== "URI: https://botnet.family/me" || lines[6] !== "Version: 1" || lines[7] !== "Chain ID: 8453" ||
      !/^Nonce: [0-9a-f]{64}$/.test(lines[8]!) || !/^Request ID: [0-9a-f]{32}$/.test(lines[12]!)) return false;
  const issued = lines[9]!.match(/^Issued At: (.+)$/)?.[1];
  const expiration = lines[10]!.match(/^Expiration Time: (.+)$/)?.[1];
  const notBefore = lines[11]!.match(/^Not Before: (.+)$/)?.[1];
  if (!issued || !notBefore || !expiration || issued !== notBefore || !isIsoMs(issued)) return false;
  const issuedMs = Date.parse(issued);
  return Number.isFinite(issuedMs) && new Date(issuedMs + 60 * 60_000).toISOString() === expiration;
}

export function parseSessionDescriptor(value: unknown, address: string, nowMs = Date.now()): WalletSessionDescriptor | null {
  let expected: Address;
  try { expected = getAddress(address); } catch { return null; }
  if (!isRecord(value) || Object.keys(value).sort().join(",") !== "address,chainId,csrfToken,expiresAtMs,idleExpiresAtMs,schemaVersion" ||
      value.schemaVersion !== 2 || value.chainId !== AUTH_CHAIN_ID || value.address !== expected ||
      !Number.isSafeInteger(value.expiresAtMs) || !Number.isSafeInteger(value.idleExpiresAtMs) ||
      (value.expiresAtMs as number) <= nowMs || (value.idleExpiresAtMs as number) <= nowMs ||
      (value.idleExpiresAtMs as number) > (value.expiresAtMs as number) ||
      typeof value.csrfToken !== "string" || !CSRF_PATTERN.test(value.csrfToken)) return null;
  return value as unknown as WalletSessionDescriptor;
}

export function createWalletAuthClient(options: WalletAuthClientOptions): WalletAuthClient {
  const fetchImpl = options.fetch ?? fetch;
  const request: WalletAuthFetch = (input, init) => fetchImpl(input, init);
  const checkEnabled = () => { if (!options.enabled) throw new Error("Wallet sign-in is disabled"); };
  const send = async (path: string, init: RequestInit) => {
    checkEnabled();
    try {
      return await boundedRequest(async (signal) => {
        const response = await request(`${CONTROL_ORIGIN}${path}`, {
          ...init,
          credentials: "include",
          cache: "no-store",
          redirect: "error",
          headers: { accept: "application/json", ...(path === CONTROL_AUTH_PATHS.challenge || path === CONTROL_AUTH_PATHS.verify ? { "x-botnet-client": CLIENT_MARKER } : {}), ...init.headers },
          signal,
        });
        const body = await readJson(response);
        if (response.status !== 200) {
          if (!isRecord(body) || Object.keys(body).sort().join(",") !== "error,requestId,schemaVersion" ||
              body.schemaVersion !== 2 || typeof body.error !== "string" || !API_ERRORS.has(body.error) ||
              API_ERROR_STATUS[body.error] !== response.status ||
              typeof body.requestId !== "string" || !/^[0-9a-f]{32}$/.test(body.requestId)) {
            throw new WalletAuthorizationError("unavailable");
          }
          throw new WalletAuthorizationError(body.error);
        }
        return body;
      }, init.signal as AbortSignal | undefined);
    } catch (error) {
      if (error instanceof WalletAuthorizationError) throw error;
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      throw new WalletAuthorizationError("unavailable");
    }
  };
  return {
    reservationAttempt(input) {
      return createWalletReservationAttempt({ enabled: options.enabled, deployment: options.deployment ?? null, request, input });
    },
    async challenge(address, signal) {
      const canonical = canonicalAddress(address);
      const body = await send(CONTROL_AUTH_PATHS.challenge, {
        method: "POST", signal,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ schemaVersion: 2, address: canonical }),
      });
      if (!isRecord(body) || Object.keys(body).sort().join(",") !== "message,preauthCsrfToken,schemaVersion" ||
          body.schemaVersion !== 2 || typeof body.message !== "string" || !parseCanonicalChallenge(body.message, canonical) ||
          typeof body.preauthCsrfToken !== "string" || !CSRF_PATTERN.test(body.preauthCsrfToken)) {
        throw new Error("Invalid wallet challenge response");
      }
      return { message: body.message, preauthCsrfToken: body.preauthCsrfToken };
    },
    async verify(address, message, signature, preauthCsrfToken, signal) {
      const canonical = canonicalAddress(address);
      if (!parseCanonicalChallenge(message, canonical) || !CSRF_PATTERN.test(preauthCsrfToken) ||
          !/^0x(?:[0-9a-fA-F]{2}){0,4096}$/.test(signature)) throw new Error("Invalid wallet proof request");
      const body = await send(CONTROL_AUTH_PATHS.verify, {
        method: "POST", signal,
        headers: { "content-type": "application/json", "x-csrf-token": preauthCsrfToken },
        body: JSON.stringify({ schemaVersion: 2, message, signature }),
      });
      const descriptor = parseSessionDescriptor(body, canonical);
      if (!descriptor) throw new Error("Invalid wallet session response");
      return descriptor;
    },
    async session(address, csrfToken, signal) {
      const canonical = canonicalAddress(address);
      if (!CSRF_PATTERN.test(csrfToken)) throw new Error("Invalid wallet CSRF token");
      const body = await send(CONTROL_AUTH_PATHS.session, { method: "GET", signal, headers: { "x-csrf-token": csrfToken } });
      const descriptor = parseSessionDescriptor(body, canonical);
      if (!descriptor) throw new Error("Invalid wallet session response");
      return descriptor;
    },
    async logout(csrfToken, signal) {
      if (!CSRF_PATTERN.test(csrfToken)) throw new Error("Invalid wallet CSRF token");
      const body = await send(CONTROL_AUTH_PATHS.logout, { method: "POST", signal, headers: { "content-type": "application/json", "x-csrf-token": csrfToken }, body: "{}" });
      if (!isRecord(body) || Object.keys(body).sort().join(",") !== "loggedOut,schemaVersion" || body.schemaVersion !== 2 || body.loggedOut !== true) {
        throw new WalletAuthorizationError("unavailable");
      }
    },
    async privateAgentStatus(registryAddress, agentId, csrfToken, signal) {
      if (!options.deployment || !sameAddress(registryAddress, options.deployment.registryAddress) ||
          !isCanonicalAgentId(agentId) || !CSRF_PATTERN.test(csrfToken)) throw new Error("Invalid private status request");
      const body = await send(`/v2/control/agents/8453/${encodeURIComponent(registryAddress.toLowerCase())}/${agentId}`, {
        method: "GET", signal, headers: { "x-csrf-token": csrfToken },
      });
      return parsePrivateAgentStatus(body, registryAddress, agentId);
    },
    async listWalletReservations(sessionInput, operation) {
      if (!isWalletOperation(operation)) throw new WalletReservationError("invalid_request", false);
      const finish = operation.finish;
      const isCurrent = operation.isCurrent;
      const signal = operation.signal;
      try {
        if (!options.enabled) throw new WalletReservationError("disabled", false);
        const now = Date.now();
        if (!Number.isSafeInteger(now) || !isRecord(sessionInput)) throw new WalletReservationError("invalid_request", false);
        const checkedSession = parseSessionDescriptor(sessionInput, typeof sessionInput.address === "string" ? sessionInput.address : "", now);
        const configured = options.deployment ?? null;
        if (signal.aborted || !safeCurrent(isCurrent, operation)) throw new WalletReservationError("cancelled", false);
        if (!checkedSession || checkedSession.address.toLowerCase() === ZERO_ADDRESS || !configured || !isDeploymentReference(configured)) {
          throw new WalletReservationError("invalid_request", false);
        }
        const retainedSession: WalletSessionDescriptor = Object.freeze({
          schemaVersion: 2,
          address: getAddress(checkedSession.address),
          chainId: AUTH_CHAIN_ID,
          expiresAtMs: checkedSession.expiresAtMs,
          idleExpiresAtMs: checkedSession.idleExpiresAtMs,
          csrfToken: checkedSession.csrfToken,
        });
        const retainedDeployment: DeploymentReference = Object.freeze({ ...configured });
        const current = (): void => {
          const currentNow = Date.now();
          if (signal.aborted || !safeCurrent(isCurrent, operation)) throw new WalletReservationError("cancelled", false);
          if (!Number.isSafeInteger(currentNow) || currentNow >= retainedSession.expiresAtMs || currentNow >= retainedSession.idleExpiresAtMs) {
            throw new WalletReservationError("expired", false);
          }
        };
        current();
        const result = await boundedRequest(async (requestSignal) => {
          const response = await request(`${CONTROL_ORIGIN}${RESERVATION_PATH}`, {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            redirect: "error",
            headers: { accept: "application/json", "x-csrf-token": retainedSession.csrfToken },
            signal: requestSignal,
          });
          const body = await readStrictJson(response, response.status === 200 ? RESERVATION_DISCOVERY_MAX_BYTES : 2048, requestSignal);
          return { response, body };
        }, signal);
        current();
        if (result.response.status !== 200) throw parseWalletReservationDiscoveryError(result.response.status, result.body);
        const snapshot = parseWalletReservationDiscovery(result.body, retainedSession, retainedDeployment);
        current();
        return snapshot;
      } catch (error) {
        if (error instanceof WalletReservationError) throw error;
        if (error instanceof WalletAuthorizationError) {
          const code = error.code === "unauthorized" || error.code === "forbidden" || error.code === "expired" ||
            error.code === "quota" || error.code === "unavailable" ? error.code : "unavailable";
          throw new WalletReservationError(code, false);
        }
        if (error instanceof DOMException && error.name === "AbortError") throw new WalletReservationError("cancelled", false);
        throw new WalletReservationError("unavailable", false);
      } finally {
        try { finish.call(operation); } catch { /* operation cleanup cannot replace discovery outcome */ }
      }
    },
  };
}

function createWalletReservationAttempt(options: {
  enabled: boolean;
  deployment: DeploymentReference | null;
  request: WalletAuthFetch;
  input: WalletReservationAttemptInput;
}): WalletReservationAttempt {
  if (!options.enabled) throw new WalletReservationError("disabled", false);
  const { input } = options;
  const factoryNow = Date.now();
  if (!Number.isFinite(factoryNow) || !isReservationInput(input, factoryNow)) throw new WalletReservationError("invalid_request", false);
  const deployment = options.deployment;
  if (!deployment || !isDeploymentReference(deployment)) throw new WalletReservationError("invalid_request", false);
  const checkedSession = parseSessionDescriptor(input.session, input.session.address, factoryNow);
  if (!checkedSession) throw new WalletReservationError("invalid_request", false);
  let systemPromptHash: `0x${string}`;
  let promptPlan: V2Utf8PromptPlan;
  try {
    if (!/^0x[0-9a-fA-F]{64}$/.test(input.systemPromptHash) || /^0x0{64}$/i.test(input.systemPromptHash)) throw new Error();
    systemPromptHash = input.systemPromptHash.toLowerCase() as `0x${string}`;
    promptPlan = encodeV2Utf8Prompt(input.additionalPrompt);
  } catch {
    throw new WalletReservationError("invalid_request", false);
  }
  const session: WalletSessionDescriptor = Object.freeze({
    schemaVersion: 2, address: getAddress(checkedSession.address), chainId: AUTH_CHAIN_ID,
    expiresAtMs: checkedSession.expiresAtMs, idleExpiresAtMs: checkedSession.idleExpiresAtMs, csrfToken: checkedSession.csrfToken,
  });
  const retainedDeployment = Object.freeze({ ...deployment });
  const requestBody: V2ReservationRequest = Object.freeze({
    schemaVersion: 2,
    idempotencyKey: input.idempotencyKey,
    tierId: input.tierId,
    name: input.name,
    additionalPrompt: Object.freeze({ encoding: 0 as const, encodedBase64: promptPlan.encodedBase64 }),
  });
  const body = JSON.stringify(requestBody);
  const lookupBody = JSON.stringify({ schemaVersion: 2, idempotencyKey: input.idempotencyKey, lookupOnly: true });
  if (new TextEncoder().encode(body).byteLength > MAX_RESERVATION_BODY_BYTES) throw new WalletReservationError("invalid_request", false);
  const retainedInput = Object.freeze({ tierId: input.tierId, name: input.name });
  const expectedNameHash = keccak256(toBytes(input.name)).toLowerCase();
  let observedReservationId: `0x${string}` | null = null;
  let inFlight: { operation: WalletOperation; promise: Promise<WalletReservationResult> } | null = null;
  let resolveInFlight: { operation: WalletOperation; promise: Promise<WalletReservationIdempotencyBinding> } | null = null;

  const attempt: WalletReservationAttempt = {
    request: requestBody,
    resolve(operation) {
      return resolveBinding(operation);
    },
    submit(operation, expectedReservationId) {
      return perform("POST", null, operation, expectedReservationId);
    },
    read(reservationId, operation) {
      return perform("GET", reservationId, operation);
    },
  };
  Object.freeze(attempt);
  return attempt;

  function resolveBinding(operation: WalletOperation): Promise<WalletReservationIdempotencyBinding> {
    if (!isWalletOperation(operation)) return Promise.reject(new WalletReservationError("invalid_request", false));
    if (resolveInFlight) {
      if (resolveInFlight.operation === operation) return resolveInFlight.promise;
      return Promise.reject(new WalletReservationError("invalid_request", false));
    }
    const capturedOperation = { signal: operation.signal, isCurrent: operation.isCurrent, finish: operation.finish, original: operation };
    const promise = runBindingRequest(capturedOperation).finally(() => {
      if (resolveInFlight?.promise === promise) resolveInFlight = null;
    });
    resolveInFlight = { operation, promise };
    return promise;
  }

  async function runBindingRequest(operation: { signal: AbortSignal; isCurrent: () => boolean; finish: () => void; original: WalletOperation }): Promise<WalletReservationIdempotencyBinding> {
    const result = await runRequest("RESOLVE", null, null, operation);
    if (!isReservationIdempotencyBinding(result)) throw new WalletReservationError("invalid_response", false);
    return result;
  }

  function perform(method: "POST" | "GET", requestedId: `0x${string}` | null, operation: WalletOperation,
    expectedReservationId?: `0x${string}`): Promise<WalletReservationResult> {
    if (method === "GET" && !isCanonicalReservationId(requestedId)) return Promise.reject(new WalletReservationError("invalid_request", false));
    if (!isWalletOperation(operation)) return Promise.reject(new WalletReservationError("invalid_request", method === "POST" && inFlight !== null));
    if (inFlight) {
      if (method === "POST" && inFlight.operation === operation) return inFlight.promise;
      return Promise.reject(new WalletReservationError("invalid_request", method === "POST"));
    }
    if (method === "GET" && observedReservationId !== null && requestedId !== observedReservationId) {
      return Promise.reject(new WalletReservationError("invalid_response", false));
    }
    if (expectedReservationId !== undefined && (!isCanonicalReservationId(expectedReservationId) ||
        observedReservationId === null || expectedReservationId !== observedReservationId)) {
      return Promise.reject(new WalletReservationError("invalid_response", false));
    }
    if (method === "POST" && observedReservationId !== null && expectedReservationId === undefined) {
      return Promise.reject(new WalletReservationError("invalid_request", false));
    }
    const signal = operation.signal;
    const isCurrent = operation.isCurrent;
    const finish = operation.finish;
    const capturedOperation = { signal, isCurrent, finish, original: operation };
    let resolveRequest!: (result: WalletReservationResult) => void;
    let rejectRequest!: (error: unknown) => void;
    const request = new Promise<WalletReservationResult>((resolve, reject) => { resolveRequest = resolve; rejectRequest = reject; });
    const settled = request.finally(() => {
      try { finish.call(operation); } catch { /* operation cleanup cannot replace the request outcome */ }
      if (inFlight?.promise === settled) inFlight = null;
    });
    if (method === "POST") inFlight = { operation, promise: settled };
    void runRequest(method, requestedId, expectedReservationId ?? null, capturedOperation)
      .then((value) => resolveRequest(value as WalletReservationResult), rejectRequest);
    return settled;
  }

  async function runRequest(
    method: "POST" | "GET" | "RESOLVE",
    requestedId: `0x${string}` | null,
    expectedReservationId: `0x${string}` | null,
    op: { signal: AbortSignal; isCurrent: () => boolean; finish: () => void; original: WalletOperation },
  ): Promise<WalletReservationResult | WalletReservationIdempotencyBinding> {
    const startedAt = Date.now();
    const deadlineAt = startedAt + REQUEST_TIMEOUT_MS;
    const controller = new AbortController();
    let timedOut = false;
    let dispatched = false;
    const readerOwner: { current: ReadableStreamDefaultReader<Uint8Array> | null } = { current: null };
    let bodyDone = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let abortHandler: (() => void) | null = null;
    let rejectInterrupt!: (reason: WalletReservationError) => void;
    const interrupt = new Promise<never>((_resolve, reject) => { rejectInterrupt = reject; });
    void interrupt.catch(() => undefined);
    const check = (): void => {
      if (op.signal.aborted || !safeCurrent(op.isCurrent, op.original)) throw new WalletReservationError("cancelled", method === "POST" && dispatched);
      const now = Date.now();
      if (!Number.isFinite(now) || !Number.isFinite(startedAt) || now >= session.expiresAtMs || now >= session.idleExpiresAtMs) {
        throw new WalletReservationError("expired", method === "POST" && dispatched);
      }
      if (now >= deadlineAt) throw new WalletReservationError("timeout", method === "POST" && dispatched);
    };
    const awaitOwned = async <T>(promise: Promise<T>): Promise<T> => {
      void promise.catch(() => undefined);
      check();
      const value = await Promise.race([promise, interrupt]);
      check();
      return value;
    };
    abortHandler = () => {
      controller.abort();
      rejectInterrupt(new WalletReservationError("cancelled", method === "POST" && dispatched));
    };
    if (op.signal.aborted) abortHandler();
    else op.signal.addEventListener("abort", abortHandler, { once: true });
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
      rejectInterrupt(new WalletReservationError("timeout", method === "POST" && dispatched));
    }, REQUEST_TIMEOUT_MS);
    try {
      check();
      if (method === "GET" && observedReservationId !== null && requestedId !== observedReservationId) throw new WalletReservationError("invalid_response", false);
      const path = method === "GET" ? `${RESERVATION_PATH}/${requestedId}` : RESERVATION_PATH;
      const postBody = expectedReservationId === null ? body : JSON.stringify({ ...requestBody, expectedReservationId });
      const init: RequestInit = method === "POST" || method === "RESOLVE" ? {
        method: "POST", credentials: "include", cache: "no-store", redirect: "error", signal: controller.signal,
        headers: { accept: "application/json", "content-type": "application/json", "x-csrf-token": session.csrfToken },
        body: method === "RESOLVE" ? lookupBody : postBody,
      } : {
        method: "GET", credentials: "include", cache: "no-store", redirect: "error", signal: controller.signal,
        headers: { accept: "application/json", "x-csrf-token": session.csrfToken },
      };
      check();
      dispatched = true;
      const response = await awaitOwned(options.request(`${CONTROL_ORIGIN}${path}`, init));
      const payload = await readReservationJson(response, awaitOwned, (value) => { readerOwner.current = value; }, () => { bodyDone = true; });
      check();
      if (method === "GET" && observedReservationId !== null && requestedId !== observedReservationId) throw new WalletReservationError("invalid_response", false);
      if (method === "RESOLVE") {
        if (response.status !== 200) throw parseReservationError("GET", response.status, payload, false);
        const binding = parseReservationIdempotencyBinding(payload, retainedDeployment, keccak256(toBytes(body)) as `0x${string}`);
        if (observedReservationId !== null && observedReservationId !== binding.reservationId) throw new WalletReservationError("invalid_response", false);
        observedReservationId = binding.reservationId;
        return binding;
      }
      const result = interpretReservationResponse(method, response.status, payload, requestedId, dispatched);
      check();
      if (result.kind === "voucher") validateVoucherBinding(result, session, retainedDeployment, retainedInput, systemPromptHash, expectedNameHash, promptPlan);
      if (observedReservationId !== null && result.reservationId !== observedReservationId) {
        throw new WalletReservationError("invalid_response", method === "POST" && dispatched);
      }
      observedReservationId ??= result.reservationId;
      return deepFreezeReservationResult(result);
    } catch (error) {
      const mayHaveReserved = method === "POST" && expectedReservationId === null && dispatched;
      if (error instanceof WalletReservationError) throw new WalletReservationError(error.code, mayHaveReserved);
      if (timedOut) throw new WalletReservationError("timeout", mayHaveReserved);
      if (op.signal.aborted || controller.signal.aborted) throw new WalletReservationError("cancelled", mayHaveReserved);
      throw new WalletReservationError("unavailable", mayHaveReserved);
    } finally {
      if (timer !== null) clearTimeout(timer);
      if (abortHandler) op.signal.removeEventListener("abort", abortHandler);
      const reader = readerOwner.current;
      if (reader && !bodyDone) {
        try {
          const cancellation = reader.cancel();
          void cancellation.catch(() => undefined);
        } catch { /* cancellation is bounded and observed */ }
      }
    }
  }

  async function readReservationJson(
    response: Response,
    awaitOwned: <T>(promise: Promise<T>) => Promise<T>,
    setReader: (reader: ReadableStreamDefaultReader<Uint8Array>) => void,
    setDone: () => void,
  ): Promise<unknown> {
    const contentType = response.headers.get("content-type")?.split(";", 1)[0]?.trim().toLowerCase();
    if (contentType !== "application/json") throw new WalletReservationError("invalid_response", true);
    const declaredLength = response.headers.get("content-length");
    if (declaredLength !== null && (!/^(?:0|[1-9][0-9]*)$/.test(declaredLength) || BigInt(declaredLength) > BigInt(RESERVATION_MAX_BYTES))) {
      throw new WalletReservationError("invalid_response", true);
    }
    if (!response.body) throw new WalletReservationError("invalid_response", true);
    const reader = response.body.getReader();
    setReader(reader);
    const chunks: Uint8Array[] = [];
    let total = 0;
    for (;;) {
      const next = await awaitOwned(reader.read());
      if (next.done) { setDone(); break; }
      total += next.value.byteLength;
      if (total > RESERVATION_MAX_BYTES) throw new WalletReservationError("invalid_response", true);
      chunks.push(next.value);
    }
    const bytes = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
    let text: string;
    try { text = new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(bytes); }
    catch { throw new WalletReservationError("invalid_response", true); }
    if (text.charCodeAt(0) === 0xfeff) throw new WalletReservationError("invalid_response", true);
    try { scanNoDuplicateKeys(text); return JSON.parse(text) as unknown; }
    catch { throw new WalletReservationError("invalid_response", true); }
  }
}

const RESERVATION_ENVELOPE_KEYS = ["schemaVersion", "reservationId", "state", "voucher", "signature", "deployment"] as const;
const RESERVATION_THIN_KEYS = ["schemaVersion", "reservationId", "state"] as const;
const VOUCHER_KEYS = [
  "creator", "payer", "reservationId", "capacitySlotId", "slotEpoch", "tierId", "price", "feeReceiver", "nameHash",
  "promptEncoding", "promptChunks", "promptRoot", "encodedPromptDigest", "decodedPromptDigest", "encodedPromptBytes",
  "decodedPromptBytes", "systemPromptHash", "runtimePolicyHash", "accountPolicyHash", "settlementPolicyHash",
  "quoteSignerEpoch", "quoteNonce", "issuedAt", "payBefore", "uploadBefore", "fulfillBefore",
] as const;
const MAX_UINT64 = (1n << 64n) - 1n;
const MAX_UINT96 = (1n << 96n) - 1n;
const MAX_UINT256 = (1n << 256n) - 1n;
const ZERO_ADDRESS = `0x${"0".repeat(40)}`;
const ZERO_BYTES32 = `0x${"0".repeat(64)}`;

function isReservationInput(value: unknown, nowMs: number): value is WalletReservationAttemptInput {
  try {
    if (!isRecord(value) || !exactKeys(value, ["session", "idempotencyKey", "tierId", "name", "additionalPrompt", "systemPromptHash"])) return false;
    if (typeof value.idempotencyKey !== "string" || !/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(value.idempotencyKey) ||
        typeof value.tierId !== "number" || !Number.isSafeInteger(value.tierId) || value.tierId < 0 || value.tierId > 0xffff_ffff ||
        typeof value.name !== "string" || !/^[A-Za-z0-9 _.-]{1,48}$/.test(value.name) ||
        typeof value.additionalPrompt !== "string" || typeof value.systemPromptHash !== "string" ||
        !/^0x[0-9a-fA-F]{64}$/.test(value.systemPromptHash) || value.systemPromptHash.toLowerCase() === ZERO_BYTES32) return false;
    const session = value.session;
    if (!isRecord(session) || !exactKeys(session, ["schemaVersion", "address", "chainId", "expiresAtMs", "idleExpiresAtMs", "csrfToken"])) return false;
    return parseSessionDescriptor(session, typeof session.address === "string" ? session.address : "", nowMs) !== null;
  } catch { return false; }
}

function isWalletOperation(value: unknown): value is WalletOperation {
  return isRecord(value) && value.signal instanceof AbortSignal && typeof value.isCurrent === "function" && typeof value.finish === "function";
}

function safeCurrent(isCurrent: () => boolean, receiver: WalletOperation): boolean {
  try { return isCurrent.call(receiver) === true; } catch { return false; }
}

function exactKeys(value: Record<string, unknown>, expected: readonly string[]): boolean {
  const keys = Object.keys(value);
  return keys.length === expected.length && keys.every(key => expected.includes(key)) && expected.every(key => Object.hasOwn(value, key));
}

function isCanonicalReservationId(value: unknown): value is `0x${string}` {
  return typeof value === "string" && /^0x[0-9a-f]{64}$/.test(value) && value !== ZERO_BYTES32;
}

function isReservationIdempotencyBinding(value: unknown): value is WalletReservationIdempotencyBinding {
  return isRecord(value) && value.schemaVersion === 2 && isCanonicalReservationId(value.reservationId) &&
    typeof value.requestHash === "string" && /^0x[0-9a-f]{64}$/.test(value.requestHash) && isDeploymentReference(value.deployment);
}

function parseReservationIdempotencyBinding(value: unknown, deployment: DeploymentReference,
  expectedRequestHash: `0x${string}`): WalletReservationIdempotencyBinding {
  if (!isRecord(value) || !exactKeys(value, ["schemaVersion", "reservationId", "state", "requestHash", "deployment"]) ||
      value.schemaVersion !== 2 || !isCanonicalReservationId(value.reservationId) ||
      !["signing", "quoted", "paid", "reconciling", "released", "converted"].includes(String(value.state)) ||
      typeof value.requestHash !== "string" || !/^0x[0-9a-f]{64}$/.test(value.requestHash) ||
      !isDeploymentReference(value.deployment) || !sameDeployment(value.deployment, deployment) || value.requestHash !== expectedRequestHash) {
    throw new WalletReservationError("invalid_response", false);
  }
  return Object.freeze({ schemaVersion: 2, reservationId: value.reservationId, state: value.state as WalletReservationRecoveryStatus,
    requestHash: value.requestHash as `0x${string}`, deployment: Object.freeze({ ...value.deployment }) });
}

function interpretReservationResponse(
  method: "POST" | "GET", status: number, value: unknown, requestedId: `0x${string}` | null, dispatched: boolean,
): WalletReservationResult {
  const successStatus = method === "POST" ? status === 200 || status === 201 : status === 200;
  if (!successStatus) throw parseReservationError(method, status, value, dispatched);
  if (!isRecord(value) || value.schemaVersion !== 2 || !isCanonicalReservationId(value.reservationId) || typeof value.state !== "string") {
    throw new WalletReservationError("invalid_response", method === "POST" && dispatched);
  }
  if (method === "GET" && requestedId !== value.reservationId) throw new WalletReservationError("invalid_response", false);
  if (method === "POST" && status === 201 && value.state !== "quoted") throw new WalletReservationError("invalid_response", true);

  if (exactKeys(value, RESERVATION_THIN_KEYS)) {
    const allowed = method === "POST" ? status === 200 && (value.state === "converted" || value.state === "released") :
      value.state === "paid" || value.state === "converted" || value.state === "released";
    if (!allowed) throw new WalletReservationError("invalid_response", method === "POST" && dispatched);
    return Object.freeze({ kind: "state", schemaVersion: 2, reservationId: value.reservationId, state: value.state }) as WalletReservationResult;
  }
  if (!exactKeys(value, RESERVATION_ENVELOPE_KEYS) || (value.state !== "quoted" && value.state !== "paid") ||
      !isRecord(value.voucher) || !exactKeys(value.voucher, VOUCHER_KEYS) || !isDeploymentReference(value.deployment) ||
      typeof value.signature !== "string" || !/^0x(?:[0-9a-fA-F]{2}){65}$/.test(value.signature)) {
    throw new WalletReservationError("invalid_response", method === "POST" && dispatched);
  }
  if (method === "POST" && status === 201 && value.state !== "quoted") throw new WalletReservationError("invalid_response", true);
  const parsedVoucher = parseReservationVoucher(value.voucher);
  if (!parsedVoucher) throw new WalletReservationError("invalid_response", method === "POST" && dispatched);
  const deployment = Object.freeze({ ...value.deployment });
  return {
    kind: "voucher", schemaVersion: 2, reservationId: value.reservationId, state: value.state,
    voucher: parsedVoucher, signature: value.signature.toLowerCase() as `0x${string}`, deployment,
  };
}

function parseReservationVoucher(value: Record<string, unknown>): V2AdmissionVoucher | null {
  const creator = canonicalVoucherAddress(value.creator, true);
  const payer = canonicalVoucherAddress(value.payer, true);
  const feeReceiver = canonicalVoucherAddress(value.feeReceiver, false);
  if (!creator || !payer || !feeReceiver || !isCanonicalReservationId(value.reservationId) ||
      !isBytes32(value.capacitySlotId) || value.capacitySlotId.toLowerCase() === ZERO_BYTES32 ||
      !isBytes32(value.nameHash) || value.nameHash.toLowerCase() === ZERO_BYTES32 ||
      value.promptEncoding !== 0 || !isUint32(value.tierId) || !isUint32(value.promptChunks) ||
      !isUint32(value.encodedPromptBytes) || !isUint32(value.decodedPromptBytes) ||
      !isBytes32(value.promptRoot, true) || !isBytes32(value.encodedPromptDigest) || !isBytes32(value.decodedPromptDigest) ||
      !isBytes32(value.systemPromptHash) || !isBytes32(value.runtimePolicyHash) || !isBytes32(value.accountPolicyHash) ||
      !isBytes32(value.settlementPolicyHash) || !isDecimalWithin(value.slotEpoch, MAX_UINT64) ||
      !isDecimalWithin(value.price, MAX_UINT96) || !isDecimalWithin(value.quoteSignerEpoch, MAX_UINT64) ||
      !isDecimalWithin(value.quoteNonce, MAX_UINT256) || !isDecimalWithin(value.issuedAt, MAX_UINT64) ||
      !isDecimalWithin(value.payBefore, MAX_UINT64) || !isDecimalWithin(value.uploadBefore, MAX_UINT64) ||
      !isDecimalWithin(value.fulfillBefore, MAX_UINT64)) return null;
  if (value.promptRoot.toLowerCase() === ZERO_BYTES32 && value.encodedPromptBytes !== 0) return null;
  const issuedAt = BigInt(value.issuedAt);
  const payBefore = BigInt(value.payBefore);
  const uploadBefore = BigInt(value.uploadBefore);
  const fulfillBefore = BigInt(value.fulfillBefore);
  if (issuedAt > payBefore || issuedAt > uploadBefore || uploadBefore > fulfillBefore) return null;
  return Object.freeze({
    creator, payer, reservationId: value.reservationId, capacitySlotId: value.capacitySlotId.toLowerCase() as `0x${string}`,
    slotEpoch: value.slotEpoch, tierId: value.tierId, price: value.price, feeReceiver,
    nameHash: value.nameHash.toLowerCase() as `0x${string}`, promptEncoding: 0, promptChunks: value.promptChunks,
    promptRoot: value.promptRoot.toLowerCase() as `0x${string}`, encodedPromptDigest: value.encodedPromptDigest.toLowerCase() as `0x${string}`,
    decodedPromptDigest: value.decodedPromptDigest.toLowerCase() as `0x${string}`, encodedPromptBytes: value.encodedPromptBytes,
    decodedPromptBytes: value.decodedPromptBytes, systemPromptHash: value.systemPromptHash.toLowerCase() as `0x${string}`,
    runtimePolicyHash: value.runtimePolicyHash.toLowerCase() as `0x${string}`, accountPolicyHash: value.accountPolicyHash.toLowerCase() as `0x${string}`,
    settlementPolicyHash: value.settlementPolicyHash.toLowerCase() as `0x${string}`, quoteSignerEpoch: value.quoteSignerEpoch,
    quoteNonce: value.quoteNonce, issuedAt: value.issuedAt, payBefore: value.payBefore,
    uploadBefore: value.uploadBefore, fulfillBefore: value.fulfillBefore,
  });
}

function validateVoucherBinding(
  result: Extract<WalletReservationResult, { kind: "voucher" }>,
  session: WalletSessionDescriptor,
  deployment: DeploymentReference,
  input: { tierId: number; name: string },
  systemPromptHash: string,
  expectedNameHash: string,
  plan: ReturnType<typeof encodeV2Utf8Prompt>,
): void {
  const voucher = result.voucher;
  if (result.reservationId !== voucher.reservationId || voucher.creator !== session.address.toLowerCase() || voucher.payer !== session.address.toLowerCase() ||
      voucher.tierId !== input.tierId || voucher.nameHash !== expectedNameHash || voucher.systemPromptHash !== systemPromptHash ||
      voucher.promptEncoding !== plan.encoding || voucher.promptChunks !== plan.chunkCount || voucher.promptRoot !== plan.root.toLowerCase() ||
      voucher.encodedPromptDigest !== plan.encodedDigest.toLowerCase() || voucher.decodedPromptDigest !== plan.decodedDigest.toLowerCase() ||
      voucher.encodedPromptBytes !== plan.encodedBytes || voucher.decodedPromptBytes !== plan.decodedBytes ||
      !sameDeployment(result.deployment, deployment)) throw new WalletReservationError("invalid_response", false);
}

function deepFreezeReservationResult<T extends WalletReservationResult>(result: T): T {
  if (result.kind === "voucher") {
    Object.freeze(result.voucher);
    Object.freeze(result.deployment);
  }
  return Object.freeze(result);
}

function canonicalVoucherAddress(value: unknown, nonzero: boolean): Address | null {
  if (typeof value !== "string" || !/^0x[0-9a-fA-F]{40}$/.test(value)) return null;
  try {
    const address = getAddress(value);
    if (nonzero && address.toLowerCase() === ZERO_ADDRESS) return null;
    return address.toLowerCase() as Address;
  } catch { return null; }
}

function isBytes32(value: unknown, zeroAllowed = false): value is `0x${string}` {
  return typeof value === "string" && /^0x[0-9a-fA-F]{64}$/.test(value) && (zeroAllowed || value.toLowerCase() !== ZERO_BYTES32);
}
function isUint32(value: unknown): value is number { return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= 0xffff_ffff; }
function isDecimalWithin(value: unknown, max: bigint): value is string {
  return typeof value === "string" && value.length <= 78 && /^(?:0|[1-9][0-9]*)$/.test(value) && BigInt(value) <= max;
}

function scanNoDuplicateKeys(text: string): void {
  let i = 0;
  const ws = () => { while (/[\u0009\u000a\u000d\u0020]/.test(text[i] ?? "")) i++; };
  const string = (): string => {
    const start = i++;
    while (i < text.length) {
      const c = text[i++]!;
      if (c === "\\") { if (i >= text.length) throw new SyntaxError(); i++; continue; }
      if (c === '"') return JSON.parse(text.slice(start, i)) as string;
      if (c.charCodeAt(0) < 0x20) throw new SyntaxError();
    }
    throw new SyntaxError();
  };
  const value = (): void => {
    ws();
    if (text[i] === '"') { string(); return; }
    if (text[i] === "{") {
      i++; ws(); const seen = new Set<string>();
      if (text[i] === "}") { i++; return; }
      for (;;) {
        ws(); if (text[i] !== '"') throw new SyntaxError();
        const key = string(); if (seen.has(key)) throw new SyntaxError(); seen.add(key);
        ws(); if (text[i++] !== ":") throw new SyntaxError(); value(); ws();
        const c = text[i++]; if (c === "}") return; if (c !== ",") throw new SyntaxError();
      }
    }
    if (text[i] === "[") {
      i++; ws(); if (text[i] === "]") { i++; return; }
      for (;;) { value(); ws(); const c = text[i++]; if (c === "]") return; if (c !== ",") throw new SyntaxError(); }
    }
    for (const word of ["true", "false", "null"]) if (text.startsWith(word, i)) { i += word.length; return; }
    const number = /^-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?(?:[eE][+-]?[0-9]+)?/.exec(text.slice(i));
    if (!number) throw new SyntaxError(); i += number[0].length;
  };
  value(); ws(); if (i !== text.length) throw new SyntaxError();
}

function parseReservationError(method: "POST" | "GET", status: number, value: unknown, dispatched: boolean): WalletReservationError {
  if (!isRecord(value) || !exactKeys(value, ["schemaVersion", "error", "requestId"]) || value.schemaVersion !== 2 ||
      typeof value.error !== "string" || typeof value.requestId !== "string" || !/^[0-9a-f]{32}$/.test(value.requestId)) {
    return new WalletReservationError("invalid_response", method === "POST" && dispatched);
  }
  if (method === "POST" && status === 409 && value.error === "unavailable") return new WalletReservationError("conflict", true);
  if (method === "GET" && status === 404 && value.error === "unavailable") return new WalletReservationError("not_found", false);
  const expectedStatus: Partial<Record<string, number>> = {
    invalid_request: 400, unauthorized: 401, expired: 401, invalid_signature: 401,
    forbidden: 403, quota: 429, unavailable: 503,
  };
  if (expectedStatus[value.error] !== status) return new WalletReservationError("invalid_response", method === "POST" && dispatched);
  return new WalletReservationError(value.error as WalletReservationErrorCode, method === "POST" && dispatched);
}

function canonicalAddress(address: string): Address {
  try { return getAddress(address); } catch { throw new Error("Invalid wallet address"); }
}
export function isCanonicalAgentId(value: string): boolean {
  return /^(0|[1-9][0-9]*)$/.test(value) && BigInt(value) <= MAX_AGENT_ID;
}
function isIsoMs(value: string): boolean {
  const ms = Date.parse(value);
  return Number.isFinite(ms) && new Date(ms).toISOString() === value;
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isAddress(value: unknown): value is `0x${string}` { return typeof value === "string" && /^0x[0-9a-f]{40}$/.test(value); }
function sameAddress(left: string, right: string): boolean { return left.toLowerCase() === right.toLowerCase() && /^0x[0-9a-fA-F]{40}$/.test(left); }
function isDeploymentReference(value: unknown): value is DeploymentReference {
  if (!isRecord(value) || Object.keys(value).sort().join(",") !== "chainId,deploymentBlockHash,deploymentBlockNumber,deploymentTransactionHash,protocol,registryAddress,registryRuntimeCodeHash,schemaVersion,sourceCommitment") return false;
  return value.schemaVersion === 1 && value.protocol === "registry-v2" && value.chainId === 8453 &&
    typeof value.registryAddress === "string" && /^0x[0-9a-f]{40}$/.test(value.registryAddress) && value.registryAddress !== `0x${"0".repeat(40)}` &&
    typeof value.deploymentBlockNumber === "string" && /^(0|[1-9][0-9]*)$/.test(value.deploymentBlockNumber) && BigInt(value.deploymentBlockNumber) <= BigInt(Number.MAX_SAFE_INTEGER) &&
    typeof value.deploymentBlockHash === "string" && /^0x[0-9a-f]{64}$/.test(value.deploymentBlockHash) &&
    typeof value.deploymentTransactionHash === "string" && /^0x[0-9a-f]{64}$/.test(value.deploymentTransactionHash) &&
    typeof value.registryRuntimeCodeHash === "string" && /^0x[0-9a-f]{64}$/.test(value.registryRuntimeCodeHash) &&
    typeof value.sourceCommitment === "string" && /^[0-9a-f]{64}$/.test(value.sourceCommitment);
}
function sameDeployment(left: DeploymentReference | null, right: DeploymentReference | null): boolean {
  return left === null ? right === null : right !== null && left.schemaVersion === right.schemaVersion && left.protocol === right.protocol &&
    left.chainId === right.chainId && left.registryAddress === right.registryAddress && left.deploymentBlockNumber === right.deploymentBlockNumber &&
    left.deploymentBlockHash === right.deploymentBlockHash && left.deploymentTransactionHash === right.deploymentTransactionHash &&
    left.registryRuntimeCodeHash === right.registryRuntimeCodeHash && left.sourceCommitment === right.sourceCommitment;
}
const RECOVERY_ENVELOPE_KEYS = ["records", "schemaVersion"] as const;
const RECOVERY_RECORD_KEYS = ["createdAtMs", "deployment", "payer", "reservationId", "scope", "status"] as const;
const RECOVERY_STATUSES = new Set<WalletReservationRecoveryStatus>(["signing", "quoted", "paid", "reconciling", "released", "converted"]);

function parseWalletReservationDiscovery(
  value: unknown,
  session: WalletSessionDescriptor,
  configuredDeployment: DeploymentReference,
): WalletReservationRecoverySnapshot {
  if (!isRecord(value) || !exactKeys(value, RECOVERY_ENVELOPE_KEYS) || value.schemaVersion !== 2 ||
      !Array.isArray(value.records) || value.records.length > 100) throw new WalletReservationError("invalid_response", false);
  const records: WalletReservationRecoveryRecord[] = [];
  const seen = new Set<string>();
  let previous: WalletReservationRecoveryRecord | null = null;
  const expectedPayer = session.address.toLowerCase();
  for (const raw of value.records) {
    if (!isRecord(raw) || !exactKeys(raw, RECOVERY_RECORD_KEYS) ||
        !isCanonicalReservationId(raw.reservationId) || !isAddress(raw.payer) || raw.payer !== expectedPayer ||
        typeof raw.status !== "string" || !RECOVERY_STATUSES.has(raw.status as WalletReservationRecoveryStatus) ||
        typeof raw.createdAtMs !== "number" || !Number.isSafeInteger(raw.createdAtMs) || raw.createdAtMs < 0 ||
        !isDeploymentReference(raw.deployment) ||
        (raw.scope !== "configured_deployment" && raw.scope !== "foreign_deployment")) {
      throw new WalletReservationError("invalid_response", false);
    }
    if (seen.has(raw.reservationId)) throw new WalletReservationError("invalid_response", false);
    seen.add(raw.reservationId);
    const deployment: DeploymentReference = Object.freeze({ ...raw.deployment });
    const scope: WalletReservationRecoveryScope = sameDeployment(deployment, configuredDeployment) ? "configured_deployment" : "foreign_deployment";
    if (raw.scope !== scope) throw new WalletReservationError("invalid_response", false);
    const record: WalletReservationRecoveryRecord = Object.freeze({
      reservationId: raw.reservationId,
      payer: raw.payer,
      status: raw.status as WalletReservationRecoveryStatus,
      createdAtMs: raw.createdAtMs,
      deployment,
      scope,
    });
    if (previous && (record.createdAtMs < previous.createdAtMs ||
        (record.createdAtMs === previous.createdAtMs && record.reservationId <= previous.reservationId))) {
      throw new WalletReservationError("invalid_response", false);
    }
    records.push(record);
    previous = record;
  }
  return Object.freeze({ schemaVersion: 2 as const, records: Object.freeze(records) });
}

function parseWalletReservationDiscoveryError(status: number, value: unknown): WalletReservationError {
  if (!isRecord(value) || !exactKeys(value, ["error", "requestId", "schemaVersion"]) || value.schemaVersion !== 2 ||
      typeof value.error !== "string" || !API_ERRORS.has(value.error) || API_ERROR_STATUS[value.error] !== status ||
      typeof value.requestId !== "string" || !/^[0-9a-f]{32}$/.test(value.requestId)) {
    return new WalletReservationError("unavailable", false);
  }
  const allowed: readonly WalletReservationErrorCode[] = ["invalid_request", "unauthorized", "forbidden", "expired", "invalid_signature", "quota", "unavailable"];
  return new WalletReservationError(allowed.includes(value.error as WalletReservationErrorCode) ? value.error as WalletReservationErrorCode : "unavailable", false);
}

async function readStrictJson(response: Response, maxBytes: number, signal: AbortSignal): Promise<unknown> {
  const invalid = () => new WalletReservationError("invalid_response", false);
  const body = response.body;
  const cancelUnacquiredBody = (): void => {
    if (!body) return;
    try { void body.cancel().catch(() => undefined); } catch { /* response cleanup cannot replace the closed refusal */ }
  };
  if (response.headers.get("content-type")?.split(";", 1)[0]?.trim().toLowerCase() !== "application/json" || !body) {
    cancelUnacquiredBody();
    throw invalid();
  }
  const declaredLength = response.headers.get("content-length");
  if (declaredLength !== null && (!/^(?:0|[1-9][0-9]*)$/.test(declaredLength) || BigInt(declaredLength) > BigInt(maxBytes))) {
    cancelUnacquiredBody();
    throw invalid();
  }
  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  let complete = false;
  let cancellation: Promise<void> | null = null;
  const cancel = (): Promise<void> | null => {
    if (cancellation) return cancellation;
    try {
      cancellation = reader.cancel();
      void cancellation.catch(() => undefined);
      return cancellation;
    } catch { return null; }
  };
  const release = () => { try { reader.releaseLock(); } catch { /* a pending read may release after cancellation settles */ } };
  const onAbort = () => cancel();
  signal.addEventListener("abort", onAbort, { once: true });
  try {
    for (;;) {
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      const chunk = await reader.read();
      if (chunk.done) { complete = true; break; }
      total += chunk.value.byteLength;
      if (total > maxBytes) throw invalid();
      chunks.push(chunk.value);
    }
    const bytes = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
    const text = new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(bytes);
    if (text.charCodeAt(0) === 0xfeff) throw invalid();
    scanNoDuplicateKeys(text);
    return JSON.parse(text) as unknown;
  } catch (error) {
    if (error instanceof WalletReservationError || error instanceof DOMException && error.name === "AbortError") throw error;
    throw invalid();
  } finally {
    signal.removeEventListener("abort", onAbort);
    if (!complete) {
      const pendingCancellation = cancel();
      if (pendingCancellation) void pendingCancellation.then(release, release);
    }
    release();
  }
}

async function readJson(response: Response, maxBytes = 2048): Promise<unknown> {
  const contentType = response.headers.get("content-type")?.split(";", 1)[0]?.trim().toLowerCase();
  if (contentType !== "application/json") throw new WalletAuthorizationError("unavailable");
  const declaredLength = response.headers.get("content-length");
  if (declaredLength !== null && (!/^\d+$/.test(declaredLength) || Number(declaredLength) > maxBytes)) throw new WalletAuthorizationError("unavailable");
  if (!response.body) throw new WalletAuthorizationError("unavailable");
  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8", { fatal: true });
  let size = 0;
  let text = "";
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size > maxBytes) {
        throw new WalletAuthorizationError("unavailable");
      }
      text += decoder.decode(value, { stream: true });
    }
    text += decoder.decode();
  } catch (error) {
    void reader.cancel().catch(() => {});
    if (error instanceof WalletAuthorizationError) throw error;
    throw new WalletAuthorizationError("unavailable");
  }
  try { return JSON.parse(text) as unknown; } catch { throw new WalletAuthorizationError("unavailable"); }
}

async function boundedRequest<T>(operation: (signal: AbortSignal) => Promise<T>, externalSignal?: AbortSignal): Promise<T> {
  if (externalSignal?.aborted) throw new DOMException("Aborted", "AbortError");
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let externalAbort: (() => void) | null = null;
  let rejectAbort!: (cause: unknown) => void;
  const aborted = new Promise<never>((_, reject) => { rejectAbort = reject; });
  if (externalSignal) {
    externalAbort = () => {
      controller.abort();
      rejectAbort(new DOMException("Aborted", "AbortError"));
    };
    if (externalSignal.aborted) externalAbort();
    else externalSignal.addEventListener("abort", externalAbort, { once: true });
  }
  const timedOut = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new WalletAuthorizationError("unavailable"));
    }, REQUEST_TIMEOUT_MS);
  });
  try {
    return await Promise.race([operation(controller.signal), aborted, timedOut]);
  } finally {
    if (timer !== null) clearTimeout(timer);
    if (externalAbort && externalSignal) externalSignal.removeEventListener("abort", externalAbort);
  }
}
